<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import * as FileDropZone from '$lib/components/ui/file-drop-zone';
	import { MEGABYTE } from '$lib/components/ui/file-drop-zone';
	import { Button, type ButtonProps } from '$lib/components/ui/button';
	import { Alert, AlertDescription } from '$lib/components/ui/alert';
	import { CloudUpload } from '@lucide/svelte';
	import { useTest } from '$lib/components/tracking/useTest';
	import { cvUploadTest } from '$lib/components/tracking/ab-tests';
	import DropDialog from './DropDialog.svelte';

	interface Props extends Omit<ButtonProps, 'onclick' | 'type'> {
		type?: 'cv' | 'jd';
		label?: string;
		showIcon?: boolean;
	}

	let {
		type = 'cv',
		label = type === 'cv' ? 'Upload CV' : 'Drop JD',
		showIcon = true,
		size = 'sm',
		class: className,
		...restProps
	}: Props = $props();

	const test = useTest(cvUploadTest);
	const MAX_FILE_SIZE = 5 * MEGABYTE;

	let isDragging = $state(false);
	let showDialog = $state(false);
	let selectedFile = $state<File | null>(null);
	let showSuccess = $state(false);
	let showError = $state(false);
	let errorMessage = $state('');
	let fileInputRef = $state<HTMLInputElement | null>(null);

	const dropzoneText = $derived({
		title: type === 'cv' ? 'Drop your CV here' : 'Drop your Job Description here',
		subtitle:
			type === 'cv'
				? "We'll analyze it and send you personalized job matches"
				: "We'll analyze it and match you with qualified candidates"
	});

	onMount(() => {
		const handleDragEnter = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			isDragging = true;
		};

		const handleDragOver = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
		};

		const handleDragLeave = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (e.target === document || e.relatedTarget === null) {
				setTimeout(() => (isDragging = false), 200);
			}
		};

		const handleDrop = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			isDragging = false;
		};

		window.addEventListener('dragenter', handleDragEnter);
		window.addEventListener('dragover', handleDragOver);
		window.addEventListener('dragleave', handleDragLeave);
		window.addEventListener('drop', handleDrop);

		return () => {
			window.removeEventListener('dragenter', handleDragEnter);
			window.removeEventListener('dragover', handleDragOver);
			window.removeEventListener('dragleave', handleDragLeave);
			window.removeEventListener('drop', handleDrop);
		};
	});

	function handleClick() {
		if (type === 'jd') {
			goto('/drop-jd');
			return;
		}

		test.event('button-clicked');

		switch (test.variant) {
			case 'a':
				test.event('file-picker-opened');
				fileInputRef?.click();
				break;
			case 'b':
				goto('/drop-cv');
				break;
			case 'c':
				test.event('dialog-opened');
				selectedFile = null;
				showDialog = true;
				break;
		}
	}

	function handleFileInputChange(e: Event) {
		const file = (e.target as HTMLInputElement).files?.[0];
		if (file) {
			selectedFile = file;
			showDialog = true;
		}
		if (fileInputRef) fileInputRef.value = '';
	}

	async function handleDropzoneUpload(files: File[]) {
		if (!files.length) return;

		const file = files[0];
		isDragging = false;

		test.event('file-selected', file.size);

		selectedFile = file;
		showDialog = true;
	}

	function handleSuccess() {
		showSuccess = true;
		setTimeout(() => {
			showSuccess = false;
		}, 5000);
	}

	function handleError(msg: string) {
		errorMessage = msg;
		showError = true;
		setTimeout(() => {
			showError = false;
		}, 5000);
	}
</script>

<input
	bind:this={fileInputRef}
	type="file"
	accept="application/pdf"
	class="hidden"
	onchange={handleFileInputChange}
/>

<Button {size} class={className} onclick={handleClick} {...restProps}>
	{#if showIcon}
		<CloudUpload class="h-4 w-4" />
	{/if}
	{label}
</Button>

{#if isDragging && !showDialog}
	<div
		class="fixed inset-0 z-[110] flex h-full min-h-[100dvh] w-full items-center justify-center bg-white px-4 sm:px-24 md:px-36 xl:px-48"
	>
		<FileDropZone.Root
			maxFiles={1}
			maxFileSize={MAX_FILE_SIZE}
			accept="application/pdf"
			onUpload={handleDropzoneUpload}
		>
			<FileDropZone.Trigger>
				<span class="flex flex-col items-center justify-center px-8 py-12 text-center">
					<span class="mb-6 rounded-full bg-blue-100 p-6">
						<CloudUpload class="h-12 w-12 text-blue-600" />
					</span>
					<p class="mb-2 text-2xl font-bold">{dropzoneText.title}</p>
					<p class="text-lg text-gray-600">
						{dropzoneText.subtitle}
					</p>
				</span>
			</FileDropZone.Trigger>
		</FileDropZone.Root>
	</div>
{/if}

<DropDialog
	bind:open={showDialog}
	initialFile={selectedFile}
	onSuccess={handleSuccess}
	onError={handleError}
/>

{#if showSuccess}
	<div class="animate-in slide-in-from-top fixed top-4 right-4 z-50">
		<Alert class="max-w-md border-green-200 bg-green-50">
			<AlertDescription class="flex items-center gap-2 text-green-800">
				<svg class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M5 13l4 4L19 7"
					/>
				</svg>
				<span>
					{type === 'cv'
						? "CV received! We'll send you personalized job matches."
						: "JD received! We'll match you with qualified candidates."}
				</span>
			</AlertDescription>
		</Alert>
	</div>
{/if}

{#if showError}
	<div class="animate-in slide-in-from-top fixed top-4 right-4 z-50">
		<Alert class="max-w-md border-red-200 bg-red-50">
			<AlertDescription class="text-red-800">
				{errorMessage}
			</AlertDescription>
		</Alert>
	</div>
{/if}